import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { withLentBlock } from '~/hooks/apis/reporting/useGetReportingLensDocumentData';

import BlockView, { type BoardContext, blockLegend, hiddenInView } from './BlockView';
import EditableText from './EditableText';
import FiltersButton from './FiltersButton';
import LensControls, { filtersSet, hasFilterControls, placementOf } from './LensControls';
import Legend, { sameMembers } from './blocks/Legend';
import ViewFrame from './blocks/ViewFrame';
import { CardBridgeFrame } from './cardBridge';
import { htmlBoardTarget } from './htmlBoard';
import TabsView from './TabsView';
import Prose from './blocks/Prose';
import RowHeight from './RowHeight';
import RowSplitter, { widthLabel } from './RowSplitter';
import { humanise, shortDay, shownFor } from './format';
import './lens.css';
import { toggled, withEntry } from './lensState';
import { choose, groupOf } from './lensTabs';
import { applyLocally, figureMove } from './localOperations';
import useBlockDrag, { dragOperation } from './useBlockDrag';
import { type Palette, paletteOf } from './palette';
import { pageBlocks } from './editor';
import { SeriesFocusContext, useSeriesFocus } from './seriesFocus';
import type { BoardEditor } from './useBoardEdit';
import useElementWidth from './useElementWidth';
import useLandedBlocks from './useLandedBlocks';
import { boardLabels } from './titles';
import { type Block, type Envelope, type LensDocument, type LensDocumentData, type LensState, type PageEntry, type Row, isColumn, isSection, isTabs } from './types';

export type HeaderTools = { ask?: React.ReactNode; share?: React.ReactNode; download?: React.ReactNode; spill?: React.ReactNode; rail?: React.ReactNode };

type LensBoardProps = {
  document: LensDocument;
  data?: LensDocumentData;
  state: LensState;
  onStateChange?: (state: LensState) => void;
  tools?: (narrow: boolean) => HeaderTools;
  theme?: 'light' | 'dark';
  loading?: boolean;
  failed?: string;
  applying?: boolean;
  fading?: Array<string>;
  editor?: BoardEditor;
  askAbout?: (block: { id: string; title: string }, draft?: string) => void;
  askFix?: (draft: string) => void;
  inspect?: BoardContext['inspect'];
  follow?: BoardContext['follow'];
  retry?: () => void;
  asked?: string;
  certified?: boolean;
  editBlock?: (blockId: string) => void;
  editBar?: React.ReactNode;
  tabs?: Array<string>;
  onTabsChange?: (tabs: Array<string>) => void;
  everyTab?: boolean;
  selected?: string;
  onSelect?: (blockId: string | undefined) => void;
};

const FILTERS_OPEN = 'lens.filters.open';

function rememberedOpen(): boolean | undefined {
  try {
    const stored = window.localStorage.getItem(FILTERS_OPEN);
    return stored === null ? undefined : stored === 'true';
  } catch {
    return undefined;
  }
}

function rememberOpen(open: boolean) {
  try {
    window.localStorage.setItem(FILTERS_OPEN, String(open));
  } catch {
    return;
  }
}

const COMPARE_WORDS: Record<string, string> = { ly: 'last year', l2y: 'two years ago' };

const dayText = (day: string) => shortDay(day, day.slice(0, 4) !== String(new Date().getFullYear()));

export function dataUpTo(envelopes: Array<Envelope>, freshness: LensDocument['display']['freshness'] = 'auto'): string | undefined {
  if (freshness === 'hidden') return undefined;
  const today = new Date().toISOString().slice(0, 10);
  const complete = envelopes.map((envelope) => envelope.completeThrough).filter((day): day is string => Boolean(day) && day! <= today).sort()[0];
  const latest = envelopes.map((envelope) => envelope.latestAt).filter((moment): moment is string => Boolean(moment)).sort()[0];
  const moment = latest ? `${dayText(latest)}, ${latest.slice(11, 16)}` : undefined;
  if (freshness === 'time') return moment ?? (complete ? dayText(complete) : undefined);
  if (freshness === 'date') return complete ? dayText(complete) : latest ? dayText(latest) : undefined;
  return latest && (!complete || latest.slice(0, 10) > complete) ? moment : complete ? dayText(complete) : undefined;
}

function spanOf(item: Block | Array<Block>): number | undefined {
  return Array.isArray(item) ? item.find((block) => block.w)?.w : item.w;
}

function rowSpans(row: Row): Array<number> {
  const fixed = row.reduce((sum, item) => sum + (spanOf(item) ?? 0), 0);
  const flexible = row.filter((item) => !spanOf(item)).length;
  const share = flexible ? Math.max(1, Math.floor((12 - fixed) / flexible)) : 0;
  const spans = row.map((item) => spanOf(item) ?? share);
  while (spans.reduce((sum, span) => sum + span, 0) > 12 && Math.max(...spans) > 1) spans[spans.indexOf(Math.max(...spans))] -= 1;
  return spans;
}

function tracksOf(spans: Array<number>): React.CSSProperties | undefined {
  if (spans.every(Number.isInteger)) return undefined;
  const used = spans.reduce((sum, span) => sum + span, 0);
  return { '--cols': [...spans, ...(used < 12 ? [12 - used] : [])].map((span) => `minmax(0, ${span}fr)`).join(' ') } as React.CSSProperties;
}

function RowView({ row, context: board }: { row: Row; context: BoardContext }) {
  const host = useRef<HTMLDivElement>(null);
  const width = useElementWidth(host);
  const focus = useSeriesFocus();
  const [preview, setPreview] = useState<Array<number>>();
  const spans = preview ?? rowSpans(row);
  const legends = row
    .filter((item): item is Block => !isColumn(item))
    .map((block) => ({ id: block.id, items: blockLegend(block, board, (width * spans[row.indexOf(block)]) / 12) }))
    .filter((entry) => entry.items.length);
  const shared = legends.length > 1 ? sameMembers(legends.map((entry) => entry.items)) : undefined;
  const context = shared ? { ...board, sharedLegend: new Set(legends.map((entry) => entry.id)) } : board;
  return (
    <SeriesFocusContext.Provider value={shared ? focus : undefined}>
      {shared && board.palette ? (
        <div className="row-legend">
          <Legend font={board.palette.sans} items={shared} width={width} />
        </div>
      ) : null}
      <div className={`row${preview ? ' sizing' : ''}`} ref={host} style={tracksOf(spans)}>
        {preview ? (
          <div aria-hidden className="grid-guide">
            {Array.from({ length: 12 }, (_, column) => (
              <i key={column} />
            ))}
          </div>
        ) : null}
        {row.map((item, index) => (
          <div className="col" key={isColumn(item) ? item.map((block) => block.id).join('+') : item.id} style={tracksOf(spans) ? undefined : { gridColumn: `span ${spans[index]}` }}>
            {(isColumn(item) ? item : [item]).map((block) => (
              <BlockView block={block} context={context} key={block.id} />
            ))}
            {preview ? <span className="span-chip">{widthLabel(spans[index])}</span> : null}
            {board.editor ? <RowSplitter editor={board.editor} host={host} index={index} onPreview={setPreview} row={row} spans={spans} /> : null}
          </div>
        ))}
        {board.editor ? <RowHeight editor={board.editor} familyOf={(id) => board.data?.blocks[id]?.family} host={host} row={row} /> : null}
      </div>
    </SeriesFocusContext.Provider>
  );
}

function PageView({ page, context }: { page: Array<PageEntry>; context: BoardContext }) {
  return (
    <>
      {page.map((entry, index) => {
        if (isSection(entry) && pageBlocks([entry]).every((block) => hiddenInView(block, context))) return null;
        if (isSection(entry))
          return (
            <section className="section" key={entry.id}>
              <div className="sh">
                <h2>
                  <EditableText enabled={Boolean(context.editor)} label="Section title" onCommit={(section) => section && context.editor!.apply({ op: 'set', id: entry.id, patch: { section } })} value={entry.section}>
                    {entry.section}
                  </EditableText>
                </h2>
                {entry.lead || context.editor ? (
                  <span className="sub">
                    <EditableText enabled={Boolean(context.editor)} label="Section lead" onCommit={(lead) => context.editor!.apply({ op: 'set', id: entry.id, patch: { lead: lead || null } })} placeholder="Add a line under the heading" value={entry.lead ?? ''}>
                      {entry.lead}
                    </EditableText>
                  </span>
                ) : null}
              </div>
              {entry.rows.map((row, rowIndex) => (
                <RowView context={context} key={rowIndex} row={row} />
              ))}
            </section>
          );
        if (isTabs(entry)) return <TabsView context={context} group={entry} key={entry.id} renderPage={(tabPage) => <PageView context={context} page={tabPage} />} />;
        return <RowView context={context} key={index} row={entry} />;
      })}
    </>
  );
}

function filterOptions(data: LensDocumentData | undefined, column: string): Array<string> {
  if (data?.options?.[column]) return data.options[column];
  const members = new Set<string>();
  for (const envelope of Object.values(data?.queries ?? {}))
    if (envelope.columns.some((entry) => entry.name === column)) for (const row of envelope.rows) if (!row.other) members.add(String(row[column]));
  return [...members];
}

const viewChanged = (state: LensState) =>
  Boolean(state.period) || [state.filters, state.select, state.ranges, state.params].some((entry) => Object.keys(entry ?? {}).length > 0);

function layoutOffset(node: HTMLElement, root: HTMLElement): { left: number; top: number } {
  let left = 0;
  let top = 0;
  for (let at: HTMLElement | null = node; at && at !== root; at = at.offsetParent as HTMLElement | null) {
    left += at.offsetLeft;
    top += at.offsetTop;
  }
  return { left, top };
}

const OWN_TARGETS = '[data-lens-block], button, a, input, select, textarea, label, [role="button"], [role="tab"], [role="option"], [role="listbox"], .rail, .MuiPopover-root';

function HtmlView({ target, context }: { target: { viewId: string; block: Block }; context: BoardContext }) {
  const host = useRef<HTMLDivElement>(null);
  const width = useElementWidth(host);
  const planned = context.data?.blocks[target.block.id];
  const ids = planned?.queries ?? (planned?.query ? [planned.query] : []);
  const envelopes = ids.map((id) => context.data?.queries[id]).filter((entry): entry is Envelope => Boolean(entry));
  if (!planned || envelopes.length < ids.length)
    return context.failed ? <div className="fail" role="alert">These numbers didn’t load.</div> : <div aria-busy className="html-view" ref={host} />;
  const view = context.document.views[target.viewId];
  if (view?.bridge === 'cards') {
    const period = context.data?.period;
    return (
      <CardBridgeFrame
        envelopes={envelopes}
        filters={context.state.filters ?? {}}
        lens={{ id: context.data?.lensId ?? '', title: context.document.title, description: '', version: context.data?.lensVersion ?? 0 }}
        period={{ from: period?.current.from, to: period?.current.to, summary: period?.current.label || period?.label || '' }}
        view={view}
      />
    );
  }
  return (
    <div className="html-view" ref={host}>
      <ViewFrame
        block={target.block}
        envelopes={envelopes}
        fullPage
        measures={context.document.measures}
        palette={context.palette}
        pick={context.pick}
        setParam={context.setParam}
        shown={shownFor(target.block, context.document)}
        state={context.state}
        view={view}
        width={width}
      />
    </div>
  );
}

export default function LensBoard({ document, data, state, onStateChange, tools, theme = 'light', loading, failed, applying = false, fading, editor, askAbout, askFix, inspect, follow, retry, asked, certified, editBlock, editBar, tabs = [], onTabsChange, everyTab, selected, onSelect }: LensBoardProps) {
  const root = useRef<HTMLDivElement>(null);
  const narrow = useElementWidth(root) <= 760;
  const [leadOpen, setLeadOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState<boolean>();
  const [remembered] = useState(rememberedOpen);
  const [palette, setPalette] = useState<Palette>();
  useLayoutEffect(() => {
    if (root.current) setPalette(paletteOf(root.current));
  }, [theme]);

  const labels = useMemo(() => boardLabels(document, data), [data, document.measures]);

  const change = onStateChange ? (next: LensState) => onStateChange(next) : undefined;
  const landed = useLandedBlocks(document, data, applying);
  const { drag, start: dragStart, attachPreview } = useBlockDrag(editor, root, (tabId) => {
    const group = groupOf(document.page, tabId);
    if (group) onTabsChange?.(choose(group, tabId, tabs));
  });
  const dragged = dragOperation(drag);
  const page = (dragged && applyLocally(document, [dragged])?.page) || document.page;
  const lent = dragged && figureMove(dragged);
  const placed = useRef(new Map<string, { left: number; top: number }>());
  const editing = Boolean(editor);
  useLayoutEffect(() => {
    const host = root.current;
    if (!host) return;
    const next = new Map<string, { left: number; top: number }>();
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    host.querySelectorAll<HTMLElement>('[data-lens-block], .figs [data-lens-item]').forEach((node) => {
      const key = node.dataset.lensBlock ?? `${node.closest<HTMLElement>('[data-lens-block]')?.dataset.lensBlock}/${node.dataset.lensItem}`;
      const at = layoutOffset(node, host);
      const before = placed.current.get(key);
      next.set(key, at);
      if (editing && !still && before && (before.left !== at.left || before.top !== at.top))
        node.animate([{ transform: `translate(${before.left - at.left}px, ${before.top - at.top}px)` }, { transform: 'none' }], { duration: 150, easing: 'ease-out' });
    });
    placed.current = next;
  }, [page, editing]);
  const upTo = data ? dataUpTo(Object.values(data.queries), document.display.freshness) : undefined;
  const context: BoardContext = {
    document,
    data: data && lent ? withLentBlock(data, lent.from, lent.to) : data,
    state,
    palette,
    labels,
    landed,
    failed: Boolean(failed),
    fading,
    tabs: { chosen: tabs, choose: (group, tabId) => onTabsChange?.(choose(group, tabId, tabs)), every: everyTab, narrow },
    ...(editor ? { editor, selected, select: onSelect, dragStart, dragging: drag?.id } : {}),
    ...(editBlock ? { editBlock } : {}),
    ...(askAbout ? { askAbout, asked } : {}),
    ...(inspect ? { inspect, follow, asked } : {}),
    ...(change
      ? {
          setParam: (name: string, value: string | undefined) => change({ ...state, params: withEntry(state.params, name, value) }),
          setPeriod: (period: string) => change({ ...state, period }),
          pick: (column: string, member: string) => change({ ...state, select: withEntry(state.select, column, toggled(state.select?.[column], member)) }),
        }
      : {}),
  };

  const period = data?.period;
  const compareText = period?.comparisons
    .map((window) => (window.id === 'prior' ? window.label : COMPARE_WORDS[window.id] ?? window.label))
    .join(' and ');
  const filterLabel = (column: string) => document.filters.find((filter) => filter.column === column)?.label ?? labels.get(column) ?? humanise(column);
  const title = (
    <EditableText enabled={Boolean(editor)} label="Lens title" onCommit={(next) => next && editor!.apply({ op: 'set', id: 'lens', patch: { title: next } })} value={document.title}>
      {document.title}
    </EditableText>
  );
  const tick = certified ? (
    <svg aria-label="Certified Lens" className="cert" fill="none" height="18" role="img" viewBox="0 0 20 20" width="18">
      <title>Certified Lens</title>
      <circle cx="10" cy="10" r="10" fill="currentColor" />
      <path d="M5.5 10.4l3 3 6-6.4" stroke="var(--surface)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
    </svg>
  ) : null;
  const lead = editor ? (
    <div className="lead">
      <EditableText enabled label="Lead" multiline onCommit={(next) => editor.apply({ op: 'set', id: 'lens', patch: { lead: next || null } })} placeholder="Add a lead: what this Lens shows" value={document.lead ?? ''}>
        {data && document.lead ? <Prose envelopes={(data.lead ?? []).map((id) => data.queries[id]).filter(Boolean)} period={data.period} shown={shownFor({ id: 'lead' }, document)} text={document.lead} /> : document.lead}
      </EditableText>
    </div>
  ) : document.lead && data ? (
      <div
        className="lead"
        {...(narrow
          ? {
              'aria-expanded': leadOpen,
              'data-clamped': leadOpen ? undefined : '',
              onClick: () => setLeadOpen(!leadOpen),
              onKeyDown: (event: React.KeyboardEvent) => (event.key === 'Enter' || event.key === ' ') && setLeadOpen(!leadOpen),
              role: 'button',
              tabIndex: 0,
            }
          : {})}
      >
        <Prose envelopes={(data.lead ?? []).map((id) => data.queries[id]).filter(Boolean)} period={data.period} shown={shownFor({ id: 'lead' }, document)} text={document.lead} />
      </div>
    ) : null;
  const chips = [
    ...Object.entries(state.filters ?? {})
      .filter(([column]) => document.filters.find((filter) => filter.column === column)?.style !== 'segmented')
      .map(([column, values]) => ({ column, values, kind: 'filters' as const })),
    ...Object.entries(state.select ?? {}).map(([column, values]) => ({ column, values, kind: 'select' as const })),
  ];

  const placement = placementOf(document);
  const controls = { document, state, change, filterLabel, options: (column: string) => filterOptions(data, column), narrow, lens: data ? { id: data.lensId, version: data.lensVersion } : undefined, editor };
  const panelled = hasFilterControls(document) && placement === 'side';
  const open = panelled && (filtersOpen ?? (!narrow && (remembered ?? true)));
  const toggleFilters = () => {
    setFiltersOpen(!open);
    rememberOpen(!open);
  };
  const filtersButton = panelled ? <FiltersButton count={filtersSet(document, state)} onToggle={toggleFilters} open={open} /> : null;
  const panel = open ? <LensControls {...controls} onOpen={setFiltersOpen} open placement="side" withPeriod={false} /> : null;
  const docked = Boolean(panel) && !narrow;
  const parts = tools?.(narrow) ?? {};
  const cluster = [parts.ask, parts.share, parts.download, parts.spill, parts.rail];
  const toolCluster = cluster.some(Boolean) ? (
    <span className="tools">
      {cluster.map((part, index) => (
        <React.Fragment key={index}>{part}</React.Fragment>
      ))}
    </span>
  ) : null;
  const htmlMode = document.display.board === 'html';
  const htmlTarget = htmlMode ? htmlBoardTarget(document) : undefined;
  const board = (
    <>
      <PageView context={context} page={page} />
      {data?.landing ? (
        <div className="note">
          {dayText(data.landing.next)} {data.landing.late ? `is late, due ${data.landing.due}` : `arrives about ${data.landing.due}`}
        </div>
      ) : null}
    </>
  );

  return (
    <div className={`lens-doc${loading ? ' loading' : ''}${editor ? ' editing' : ''}`} data-board={htmlMode ? 'html' : undefined} data-density={document.display.density ?? 'compact'} data-frame={document.display.frame ?? 'auto'} data-lens-document data-no-dark-override data-lifting={drag?.id.includes('/') ? 'figure' : undefined} data-theme={theme} onClick={onSelect ? (event) => !(event.target as HTMLElement).closest(OWN_TARGETS) && onSelect(undefined) : undefined} ref={root}>
      {editBar}
      {htmlMode ? (
        <header className="head html-strip">
          <LensControls {...controls} onOpen={setFiltersOpen} only="period" open={false} placement="top" />
          {period ? (
            <span className="when">
              <b>{period.current.label}</b>
              {compareText ? ` vs ${compareText}` : ''}
            </span>
          ) : null}
          {toolCluster}
        </header>
      ) : document.display.tone === 'accent' ? (
        <header className="band">
          {document.display.eyebrow ? <div className="eyebrow">{document.display.eyebrow}</div> : null}
          <div>
            <h1 hidden={document.display.title === 'hidden' && !editor}>
              {title}
              {tick}
            </h1>
            {lead}
          </div>
          <div className="when">
            {period ? (
              <>
                <div className="range">
                  {period.current.label}
                </div>
                {compareText || upTo ? <div className="vs">{[compareText && `vs ${compareText}`, upTo && `data up to ${upTo}`].filter(Boolean).join(' · ')}</div> : null}
              </>
            ) : null}
            {toolCluster}
          </div>
        </header>
      ) : (
        <>
          <header className="head">
            <div className="ttl">
              <h1 hidden={document.display.title === 'hidden' && !editor}>
                {title}
                {tick}
              </h1>
              {period || upTo ? (
                <span className="when">
                  {period ? <b>{period.current.label}</b> : null}
                  {period && compareText ? ` vs ${compareText}` : ''}
                  {upTo ? `${period ? ' · ' : ''}data up to ${upTo}` : ''}
                </span>
              ) : null}
            </div>
            {toolCluster}
          </header>
          {lead}
        </>
      )}
      <div aria-label={applying ? 'Applying the change' : undefined} className={`applying${applying ? ' on' : ''}`} role={applying ? 'progressbar' : undefined} />
      {htmlMode ? null : (
      <div className="controls">
        {filtersButton}
        <LensControls {...controls} onOpen={setFiltersOpen} open={false} placement={panelled ? 'side' : 'top'} />
        {chips.filter((chip) => chip.kind === 'select' || (panelled && !open)).map((chip) => (
          <button
            className="chip set"
            disabled={!change}
            key={`${chip.kind}:${chip.column}`}
            onClick={() => change?.({ ...state, [chip.kind]: withEntry(state[chip.kind], chip.column, undefined) })}
            type="button"
          >
            {filterLabel(chip.column)}: {chip.values.join(', ')}
            <span aria-hidden className="x">
              ✕
            </span>
          </button>
        ))}
        {change && viewChanged(state) ? (
          <button className="chip reset" onClick={() => change({})} title="Back to the period and filters this Lens opens on" type="button">
            Reset
          </button>
        ) : null}
        {panel && narrow ? panel : null}
      </div>
      )}
      {failed ? (
        <div className="fail" role="alert">
          {failed}
          {retry ? (
            <button className="fix" onClick={retry} type="button">
              Try again
            </button>
          ) : null}
          {askFix ? (
            <button className="fix" onClick={() => askFix(`Fix this Lens: ${failed}`)} type="button">
              Ask askRichard to fix
            </button>
          ) : null}
        </div>
      ) : null}
      {drag ? (
        <div aria-hidden className="drag-chip" ref={attachPreview} style={{ left: 0, top: 0 }}>
          {drag.label}
        </div>
      ) : null}
      {htmlMode ? (
        htmlTarget ? <HtmlView context={context} target={htmlTarget} /> : <div className="fail" role="alert">This full-page view is not on the Lens.</div>
      ) : docked ? (
        <div className="with-rail">
          <aside aria-label="Filters" className="rail">
            {panel}
          </aside>
          <div className="rail-main">{board}</div>
        </div>
      ) : (
        board
      )}
    </div>
  );
}
